﻿import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChartData, ChartOptions } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import { AlertAnnotation } from '../../../../../model/passive/alertMoment';
import { IProcessedTemperatureData } from '../../../../../model/passive/temperatureData';

const ChartContainer = styled(Box)({
  height: '100%',
  width: '100%',
  minHeight: '200px',
  position: 'relative',
});

const NoDataContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
});

interface TemperatureDifferenceChartProps {
  data: IProcessedTemperatureData[];
  height?: number | string;
  selectedDateRange?: [Date | null, Date | null];
  chartRef?: React.MutableRefObject<any>;
  onZoomChange?: (chart: any) => void;
  alertAnnotations?: AlertAnnotation[];
  onAlertAnnotationClick?: (alertId: number) => void;
}

const GAP_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour gap threshold for segmenting data (resets filters/envelopes)
const SPAN_GAPS_MS = 21 * 60 * 1000; // max gap to bridge with a continuous line
const ENVELOPE_WINDOW_MS = 60 * 60 * 1000; // centered Â±30 min window for min/max envelope

// For each point, collects y-values within Â±ENVELOPE_WINDOW_MS/2 in the same gap-segment
// and returns parallel lower (min) and upper (max) series.
const computeWindowedEnvelope = (
  points: { x: number; y: number | null }[],
): {
  lower: { x: number; y: number | null }[];
  upper: { x: number; y: number | null }[];
} => {
  if (points.length === 0) return { lower: [], upper: [] };

  const halfWindow = ENVELOPE_WINDOW_MS / 2;

  // Assign each point a segment id based on GAP_THRESHOLD_MS
  const segIds: number[] = new Array(points.length).fill(0);
  let segId = 0;
  for (let i = 1; i < points.length; i++) {
    if (points[i].x - points[i - 1].x > GAP_THRESHOLD_MS) segId++;
    segIds[i] = segId;
  }

  const lower: { x: number; y: number | null }[] = [];
  const upper: { x: number; y: number | null }[] = [];

  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    if (point.y === null) {
      lower.push({ x: point.x, y: null });
      upper.push({ x: point.x, y: null });
      continue;
    }
    const t = point.x;
    const windowValues: number[] = [];
    for (let j = 0; j < points.length; j++) {
      if (segIds[j] !== segIds[i] || points[j].y === null) continue;
      if (Math.abs(points[j].x - t) <= halfWindow) {
        windowValues.push(points[j].y as number);
      }
    }
    lower.push({ x: point.x, y: Math.min(...windowValues) });
    upper.push({ x: point.x, y: Math.max(...windowValues) });
  }

  return { lower, upper };
};

// Centered Gaussian filter applied to y-values, restarting at gaps > GAP_THRESHOLD_MS.
// null entries are excluded from the weighted sum within each segment.
// Returns points with x as a numeric timestamp (required by Chart.js Point type).
const applyGaussianFilter = (
  points: { x: number; y: number | null }[],
  sigma: number
): { x: number; y: number | null }[] => {
  if (points.length === 0) return [];

  const radius = Math.ceil(3 * sigma);
  const kernelSize = 2 * radius + 1;
  const kernel = Array.from({ length: kernelSize }, (_, i) => {
    const d = i - radius;
    return Math.exp(-(d * d) / (2 * sigma * sigma));
  });

  const filterSegment = (segment: { x: number; y: number | null }[]): { x: number; y: number | null }[] =>
    segment.map((point, idx) => {
      if (point.y === null) return { x: point.x, y: null };
      let weightedSum = 0;
      let weightTotal = 0;
      for (let k = 0; k < kernelSize; k++) {
        const j = idx - radius + k;
        if (j >= 0 && j < segment.length && segment[j].y !== null) {
          weightedSum += (segment[j].y as number) * kernel[k];
          weightTotal += kernel[k];
        }
      }
      return { x: point.x, y: weightTotal > 0 ? weightedSum / weightTotal : null };
    });

  // Split into segments separated by gaps > GAP_THRESHOLD_MS
  const segments: { x: number; y: number | null }[][] = [];
  let current: { x: number; y: number | null }[] = [points[0]];
  for (let i = 1; i < points.length; i++) {
    const gap = points[i].x - points[i - 1].x;
    if (gap > GAP_THRESHOLD_MS) {
      segments.push(current);
      current = [];
    }
    current.push(points[i]);
  }
  segments.push(current);

  return segments.flatMap(filterSegment);
};

const GAUSSIAN_SIGMA = 2;

const THIRTY_MINUTES_MS = 30 * 60 * 1000;

const TemperatureDifferenceChart: React.FC<TemperatureDifferenceChartProps> = ({
  data,
  height = '100%',
  selectedDateRange,
  chartRef: externalChartRef,
  onZoomChange,
  alertAnnotations = [],
  onAlertAnnotationClick,
}) => {
  const { t } = useTranslation('dailyParameters');
  const internalChartRef = useRef<any>(null);
  const chartRef = externalChartRef || internalChartRef;

  // Calculate the date range in days
  const calculateRangeDays = (): number => {
    if (!selectedDateRange?.[0] || !selectedDateRange?.[1]) return 1;
    const diffMs = selectedDateRange[1].getTime() - selectedDateRange[0].getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const rangeDays = calculateRangeDays();

  // Configurable thresholds for time unit detection
  const SINGLE_DAY_THRESHOLD = 1;
  const WEEK_THRESHOLD = 7;

  // Determine time unit and display format based on range
  const getTimeConfig = () => {
    if (rangeDays <= SINGLE_DAY_THRESHOLD) {
      // Single day - show hours
      return {
        unit: 'hour' as const,
        displayFormats: {
          hour: 'HH:mm',
          day: 'dd MMM',
        },
        tooltipFormat: 'dd MMM yyyy HH:mm',
      };
    } else if (rangeDays <= WEEK_THRESHOLD) {
      // Up to a week - show hours but with day boundaries emphasized
      return {
        unit: 'hour' as const,
        displayFormats: {
          hour: 'HH:mm',
          day: 'dd MMM',
        },
        tooltipFormat: 'dd MMM yyyy HH:mm',
        stepSize: 6, // Show every 6 hours to avoid crowding
      };
    } else {
      // More than a week - show days only
      return {
        unit: 'day' as const,
        displayFormats: {
          day: 'dd MMM',
          week: 'dd MMM',
        },
        tooltipFormat: 'dd MMM yyyy',
      };
    }
  };

  const timeConfig = getTimeConfig();

  // Fixed y-axis: 8 degree range centred on the average temperature difference
  const getYAxisBounds = () => {
    const validData = data.filter(
      (item) => item.averageLeftTemperature > 0 && item.averageRightTemperature > 0
    );
    if (validData.length === 0) return {};
    const avgDiff =
      validData.reduce((sum, item) => sum + (item.averageLeftTemperature - item.averageRightTemperature), 0) /
      validData.length;
    return { min: avgDiff - 4, max: avgDiff + 4 };
  };

  const yAxisBounds = getYAxisBounds();

  // Calculate min/max to include full days
  const getAxisBounds = () => {
    if (!selectedDateRange?.[0] || !selectedDateRange?.[1]) return {};

    // Set min to start of first day (00:00:00)
    const minDate = new Date(selectedDateRange[0]);
    minDate.setHours(0, 0, 0, 0);

    // Set max to end of last day (23:59:59)
    const maxDate = new Date(selectedDateRange[1]);
    maxDate.setHours(23, 59, 59, 999);

    return {
      min: minDate.getTime(),
      max: maxDate.getTime(),
    };
  };

  const axisBounds = getAxisBounds();

  // Format data for the chart - 3 lines showing difference for each sensor position
  const formatChartData = (): ChartData<'line'> => {
    // Map data to x,y coordinates with Date objects for time scale
    const topDifference = data.map((item) => {
      const leftTop = item.leftTemperatures?.[0];
      const rightTop = item.rightTemperatures?.[0];
      return {
        x: new Date(item.date).getTime(),
        y: leftTop !== undefined && rightTop !== undefined ? leftTop - rightTop : null,
      };
    });

    const midDifference = data.map((item) => {
      const leftMid = item.leftTemperatures?.[1];
      const rightMid = item.rightTemperatures?.[1];
      return {
        x: new Date(item.date).getTime(),
        y: leftMid !== undefined && rightMid !== undefined ? leftMid - rightMid : null,
      };
    });

    const rearDifference = data.map((item) => {
      const leftRear = item.leftTemperatures?.[2];
      const rightRear = item.rightTemperatures?.[2];
      return {
        x: new Date(item.date).getTime(),
        y: leftRear !== undefined && rightRear !== undefined ? leftRear - rightRear : null,
      };
    });

    const topEnv = computeWindowedEnvelope(topDifference);
    const midEnv = computeWindowedEnvelope(midDifference);
    const rearEnv = computeWindowedEnvelope(rearDifference);

    return {
      datasets: [
        // Toes â€” upper fills down to lower (+1), lower fills false
        {
          label: '_toes_upper',
          data: applyGaussianFilter(topEnv.upper, GAUSSIAN_SIGMA),
          borderColor: 'transparent' as const,
          backgroundColor: 'rgba(33,150,243,0.15)',
          fill: '+1',
          tension: 0.4,
          pointRadius: 0,
          spanGaps: SPAN_GAPS_MS,
        },
        {
          label: '_toes_lower',
          data: applyGaussianFilter(topEnv.lower, GAUSSIAN_SIGMA),
          borderColor: 'transparent' as const,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          spanGaps: SPAN_GAPS_MS,
        },
        {
          label: t('charts.series.toesDifference'),
          data: applyGaussianFilter(topDifference, GAUSSIAN_SIGMA),
          borderColor: '#2196f3', // Blue
          backgroundColor: 'rgba(0, 0, 0, 0)',
          tension: 0.4,
          pointRadius: 0,
          spanGaps: SPAN_GAPS_MS,
        },
        // Metatarsal heads
        {
          label: '_mid_upper',
          data: applyGaussianFilter(midEnv.upper, GAUSSIAN_SIGMA),
          borderColor: 'transparent' as const,
          backgroundColor: 'rgba(255,152,0,0.15)',
          fill: '+1',
          tension: 0.4,
          pointRadius: 0,
          spanGaps: SPAN_GAPS_MS,
        },
        {
          label: '_mid_lower',
          data: applyGaussianFilter(midEnv.lower, GAUSSIAN_SIGMA),
          borderColor: 'transparent' as const,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          spanGaps: SPAN_GAPS_MS,
        },
        {
          label: t('charts.series.metatarsalHeadsDifference'),
          data: applyGaussianFilter(midDifference, GAUSSIAN_SIGMA),
          borderColor: '#ff9800', // Orange
          backgroundColor: 'rgba(0, 0, 0, 0)',
          tension: 0.4,
          pointRadius: 0,
          spanGaps: SPAN_GAPS_MS,
        },
        // Heel
        {
          label: '_rear_upper',
          data: applyGaussianFilter(rearEnv.upper, GAUSSIAN_SIGMA),
          borderColor: 'transparent' as const,
          backgroundColor: 'rgba(76,175,80,0.15)',
          fill: '+1',
          tension: 0.4,
          pointRadius: 0,
          spanGaps: SPAN_GAPS_MS,
        },
        {
          label: '_rear_lower',
          data: applyGaussianFilter(rearEnv.lower, GAUSSIAN_SIGMA),
          borderColor: 'transparent' as const,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          spanGaps: SPAN_GAPS_MS,
        },
        {
          label: t('charts.series.heelDifference'),
          data: applyGaussianFilter(rearDifference, GAUSSIAN_SIGMA),
          borderColor: '#4caf50', // Green
          backgroundColor: 'rgba(0, 0, 0, 0)',
          tension: 0.4,
          pointRadius: 0,
          spanGaps: SPAN_GAPS_MS,
        },
      ],
    };
  };

  const buildAnnotations = () => {
    const annotations: Record<string, any> = {};
    alertAnnotations.forEach((ann, i) => {
      annotations[`alert_box_${i}`] = {
        type: 'box',
        xMin: ann.timestamp - THIRTY_MINUTES_MS,
        xMax: ann.timestamp + THIRTY_MINUTES_MS,
        backgroundColor: ann.color + '26', // ~15% opacity via hex alpha
        borderWidth: 0,
        click: ann.alertId !== undefined && onAlertAnnotationClick
          ? () => onAlertAnnotationClick(ann.alertId!)
          : undefined,
        enter: ann.alertId !== undefined && onAlertAnnotationClick
          ? ({ chart }: any) => { chart.canvas.style.cursor = 'pointer'; }
          : undefined,
        leave: ann.alertId !== undefined && onAlertAnnotationClick
          ? ({ chart }: any) => { chart.canvas.style.cursor = 'default'; }
          : undefined,
      };
      annotations[`alert_line_${i}`] = {
        type: 'line',
        xMin: ann.timestamp,
        xMax: ann.timestamp,
        borderColor: ann.color,
        borderWidth: 2,
      };
    });
    return annotations;
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        ...yAxisBounds,
        title: {
          display: true,
          text: t('charts.axis.temperatureDifference'),
        },
      },
      x: {
        type: 'timeseries',
        time: {
          unit: timeConfig.unit,
          displayFormats: timeConfig.displayFormats,
          tooltipFormat: timeConfig.tooltipFormat,
        },
        ...axisBounds,
        title: {
          display: true,
          text: t('charts.axis.dateTime'),
        },
        ticks: {
          source: 'auto',
          maxRotation: 0,
          major: {
            enabled: true,
          },
          font: function(context) {
            if (context.tick && context.tick.major) {
              return {
                weight: 'bold',
              };
            }
          },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          filter: (item) => !item.text.startsWith('_'),
        },
        onClick: (_event, legendItem, legend) => {
          const chart = legend.chart;
          const mainIndex = legendItem.datasetIndex!;
          const meta = chart.getDatasetMeta(mainIndex);
          meta.hidden = !meta.hidden;
          // Toggle the two envelope datasets that immediately precede the main line
          [mainIndex - 2, mainIndex - 1].forEach((envIndex) => {
            if (envIndex >= 0) chart.getDatasetMeta(envIndex).hidden = meta.hidden;
          });
          chart.update();
        },
      },
      tooltip: {
        filter: (item) => !item.dataset.label?.startsWith('_'),
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return value !== null ? `${context.dataset.label}: ${value.toFixed(2)}°C` : '';
          },
          title: (context) => {
            if (!context[0]?.parsed.x) return '';
            const date = new Date(context[0].parsed.x);
            return date.toLocaleString();
          },
        },
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: false,
          },
          drag: {
            enabled: true,
          },
          pinch: {
            enabled: false,
          },
          mode: 'xy',
          onZoomComplete: ({ chart }: any) => {
            if (onZoomChange) {
              onZoomChange(chart);
            }
          },
        },
        pan: {
          enabled: true,
          mode: 'xy',
          modifierKey: 'shift',
          onPanComplete: ({ chart }: any) => {
            if (onZoomChange) {
              onZoomChange(chart);
            }
          },
        },
        limits: {
          x: { min: 'original', max: 'original' },
        },
      },
      annotation: {
        annotations: buildAnnotations(),
      },
    },
  };

  if (data.length === 0) {
    return (
      <NoDataContainer sx={{ height }}>
        <Typography variant="body2" color="textSecondary">
          {t('charts.noData')}
        </Typography>
      </NoDataContainer>
    );
  }

  return (
    <ChartContainer sx={{ height }}>
      <Line ref={chartRef} data={formatChartData()} options={chartOptions as any} />
    </ChartContainer>
  );
};

export default TemperatureDifferenceChart;

